/**
 * Remote data hosting catalog (catalog-v2.json) and schema resolution.
 * Specification: plans/web-player/10-r2-data-hosting.md
 */
import { joinUrl } from '../core/bundle/pump.ts';
import { VERSION } from '../version.ts';

export interface CatalogRace {
  id: string;
  season?: number;
  round?: number;
  name?: string;
  circuit?: string;
  /** imagery generation this build's tiles come from (e.g. "imagery-1"); pairs with circuits[] */
  imagery?: string;
  manifest_schema?: number;
  date?: string;
  laps?: number;
  build: string;
  bytes_total?: number;
  builds?: string[];
}

/** One entry per (id, imagery) pair: a regrade (imagery-2) coexists with races on imagery-1. */
export interface CatalogCircuit {
  id: string;
  name?: string;
  imagery?: string;
  tiles_fingerprint?: string;
  tiles_bytes?: number;
  licence?: string;
  attribution?: string;
  imagery_vintage?: number;
  grades?: string[];
}

/** v0.7.0 renamed the bundle keys, so it reads its own catalog; v0.6.x players keep catalog.json. */
export const CATALOG_FILE = 'catalog-v2.json';

export interface Catalog {
  schema: number;
  default_race: string;
  min_player: string;
  races: CatalogRace[];
  circuits?: CatalogCircuit[];
}

export type CatalogErrorKind =
  | 'network'
  | 'http_404'
  | 'http_error'
  | 'html_fallback'
  | 'invalid_json'
  | 'invalid_schema'
  | 'schema_too_new'
  | 'min_player'
  | 'race_not_found'
  /** the legacy no-catalog bundle was asked for another race (M8: fail loudly, not Monza mislabelled) */
  | 'legacy_race';

export class CatalogError extends Error {
  constructor(
    message: string,
    readonly kind: CatalogErrorKind,
    readonly status?: number,
    readonly minPlayer?: string,
  ) {
    super(message);
    this.name = 'CatalogError';
  }
}

/**
 * Compare two semver strings (e.g. "0.5.1" vs "0.5.2").
 * Returns negative if a < b, 0 if a == b, positive if a > b.
 */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string): number[] => {
    const s = v.replace(/^v/, '').split('-')[0] ?? '';
    return s.split('.').map((x) => Number.parseInt(x, 10) || 0);
  };
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Parse and validate catalog JSON text, rejecting HTML SPA fallbacks and malformed data.
 */
export function parseCatalog(text: string, contentType?: string | null): Catalog {
  if (contentType && /text\/html/i.test(contentType)) {
    throw new CatalogError('Catalog returned HTML content-type (SPA fallback)', 'html_fallback');
  }
  const trimmed = text.trim();
  if (trimmed.startsWith('<')) {
    throw new CatalogError('Catalog body appears to be HTML (SPA fallback)', 'html_fallback');
  }
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new CatalogError(`Invalid JSON in catalog: ${String(err)}`, 'invalid_json');
  }
  return validateCatalog(data);
}

/**
 * Validate catalog structure. Only catalog schema 1 is accepted: a future schema (2+) must be
 * GATED — an old player refuses it with a clear "update the player" error instead of silently
 * parsing fields it does not know (plan 10: the min_player idea, applied to the schema itself).
 */
export function validateCatalog(data: unknown): Catalog {
  if (!data || typeof data !== 'object') {
    throw new CatalogError('Catalog must be a JSON object', 'invalid_schema');
  }
  const c = data as Record<string, unknown>;
  if (typeof c.schema !== 'number' || !Number.isInteger(c.schema) || c.schema < 1) {
    throw new CatalogError('Catalog missing valid schema number', 'invalid_schema');
  }
  if (c.schema > 1) {
    throw new CatalogError(
      `Catalog schema ${String(c.schema)} is newer than this player supports (player parses schema 1). Please update the player.`,
      'schema_too_new',
      undefined,
      c.min_player && typeof c.min_player === 'string' ? c.min_player : undefined,
    );
  }
  if (typeof c.default_race !== 'string' || c.default_race.trim() === '') {
    throw new CatalogError('Catalog missing default_race', 'invalid_schema');
  }
  if (typeof c.min_player !== 'string' || c.min_player.trim() === '') {
    throw new CatalogError('Catalog missing min_player', 'invalid_schema');
  }
  if (!Array.isArray(c.races) || c.races.length === 0) {
    throw new CatalogError('Catalog races must be a non-empty array', 'invalid_schema');
  }
  for (const r of c.races) {
    if (!r || typeof r !== 'object') {
      throw new CatalogError('Invalid race entry in catalog', 'invalid_schema');
    }
    const race = r as Record<string, unknown>;
    if (typeof race.id !== 'string' || race.id.trim() === '') {
      throw new CatalogError('Race missing id', 'invalid_schema');
    }
    if (typeof race.build !== 'string' || race.build.trim() === '') {
      throw new CatalogError(`Race ${race.id} missing build`, 'invalid_schema');
    }
    if (race.imagery !== undefined && (typeof race.imagery !== 'string' || !/^imagery-[0-9]+$/.test(race.imagery))) {
      throw new CatalogError(`Race ${race.id} has bad imagery generation ${JSON.stringify(race.imagery)}`, 'invalid_schema');
    }
  }
  // circuits[]: one entry per (id, imagery) pair — the same circuit may appear once per generation,
  // but a pair must be unique (duplicates would make the fingerprint cross-check ambiguous).
  const seenCircuits = new Set<string>();
  if (c.circuits !== undefined) {
    if (!Array.isArray(c.circuits)) {
      throw new CatalogError('Catalog circuits must be an array', 'invalid_schema');
    }
    for (const ci of c.circuits) {
      if (!ci || typeof ci !== 'object') {
        throw new CatalogError('Invalid circuit entry in catalog', 'invalid_schema');
      }
      const circ = ci as Record<string, unknown>;
      if (typeof circ.id !== 'string' || circ.id.trim() === '') {
        throw new CatalogError('Circuit missing id', 'invalid_schema');
      }
      if (circ.imagery !== undefined && (typeof circ.imagery !== 'string' || !/^imagery-[0-9]+$/.test(circ.imagery))) {
        throw new CatalogError(`Circuit ${circ.id} has bad imagery generation ${JSON.stringify(circ.imagery)}`, 'invalid_schema');
      }
      const key = `${circ.id}\u0000${circ.imagery ?? ''}`;
      if (seenCircuits.has(key)) {
        throw new CatalogError(`Duplicate circuits[] entry for ${circ.id} ${circ.imagery ?? ''}`, 'invalid_schema');
      }
      seenCircuits.add(key);
    }
  }
  return data as Catalog;
}

/**
 * Gate running player version against catalog min_player.
 */
export function checkMinPlayer(runningVersion: string, minPlayer: string): void {
  if (compareSemver(runningVersion, minPlayer) < 0) {
    throw new CatalogError(
      `Catalog requires player version >= ${minPlayer}, but running player is ${runningVersion}. Please update the player.`,
      'min_player',
      undefined,
      minPlayer,
    );
  }
}

/**
 * Resolve race: if requestedRace is provided, use it; otherwise pin to catalog.default_race (never newest).
 */
export function resolveRace(catalog: Catalog, requestedRace?: string): CatalogRace {
  const raceId = requestedRace && requestedRace.trim() !== '' ? requestedRace.trim() : catalog.default_race;
  const race = catalog.races.find((r) => r.id === raceId);
  if (!race) {
    throw new CatalogError(`HTTP 404: Race "${raceId}" not found in catalog`, 'race_not_found', 404);
  }
  return race;
}

/**
 * Resolve the requested race, falling back to the pinned default when the hash names a race the
 * catalog does not know (typo, retired race, stale deep link): boot must not dead-end on a 404
 * card. Returns `fellBack` so the caller can warn and rewrite the hash to the resolved race.
 * A broken default_race (not in races[]) still throws — that is a catalog bug, not a bad link.
 */
export function resolveRaceWithFallback(catalog: Catalog, requestedRace?: string): { race: CatalogRace; fellBack: boolean } {
  if (requestedRace !== undefined && requestedRace.trim() !== '') {
    try {
      return { race: resolveRace(catalog, requestedRace), fellBack: false };
    } catch (err) {
      if (err instanceof CatalogError && err.kind === 'race_not_found') {
        return { race: resolveRace(catalog), fellBack: true };
      }
      throw err;
    }
  }
  return { race: resolveRace(catalog), fellBack: false };
}

/**
 * The circuits[] entry for one race: matched by the (id, imagery) pair (races[i].imagery, falling
 * back to the build's tiles.base when the race entry predates the field). Returns undefined when
 * the catalog has no entry for that pair — e.g. an old-shape catalog — never a wrong generation.
 */
export function circuitForRace(catalog: Catalog, race: CatalogRace, manifestTilesBase?: string): CatalogCircuit | undefined {
  let imagery = race.imagery;
  if (!imagery && manifestTilesBase) {
    const last = manifestTilesBase.split('/').pop();
    if (last && /^imagery-[0-9]+$/.test(last)) imagery = last;
  }
  return catalog.circuits?.find((ci) => ci.id === race.circuit && (imagery === undefined || ci.imagery === imagery));
}

/**
 * Fetch the catalog from the dataBase root.
 */
export async function fetchCatalog(
  dataBase: string,
  fetchFn: typeof fetch = fetch.bind(globalThis),
  signal?: AbortSignal,
): Promise<Catalog> {
  const url = joinUrl(dataBase, CATALOG_FILE);
  let res: Response;
  try {
    // `no-cache`: always revalidate (ETag). The zone's Browser Cache TTL raises the catalog's
    // max-age=60 to 4 h for browsers, which would delay a new race or a rollback for returning
    // visitors; the data paths it points at are immutable and keep their long cache.
    res = await fetchFn(url, signal ? { signal, cache: 'no-cache' } : { cache: 'no-cache' });
  } catch (err) {
    throw new CatalogError(`Catalog unreachable at ${url}: ${String(err)}`, 'network');
  }
  if (res.status === 404) {
    throw new CatalogError(`HTTP 404: Catalog not found at ${url}`, 'http_404', 404);
  }
  if (!res.ok) {
    throw new CatalogError(`HTTP ${String(res.status)}: Failed to fetch catalog at ${url}`, 'http_error', res.status);
  }
  const contentType = res.headers.get('content-type');
  const text = await res.text();
  return parseCatalog(text, contentType);
}

/**
 * Check if a manifest is schema 2.
 */
export function isManifestSchema2(manifest: unknown): boolean {
  if (!manifest || typeof manifest !== 'object') return false;
  const m = manifest as { schema?: unknown };
  return m.schema === 2;
}

/**
 * Resolve tilesBase: joinUrl(dataBase, man.tiles.base) for schema 2, or bundleBase for schema 1.
 */
export function getTilesBase(dataBase: string, bundleBase: string, manifest: unknown): string {
  if (manifest && typeof manifest === 'object') {
    const m = manifest as { tiles?: { base?: unknown } };
    if (m.tiles && typeof m.tiles.base === 'string' && m.tiles.base.trim() !== '') {
      return joinUrl(dataBase, m.tiles.base);
    }
  }
  return bundleBase;
}

/** Human message for a boot failure (the loading card). Exported for tests. */
export function formatBootError(err: unknown): string {
  if (err instanceof CatalogError) {
    if (err.kind === 'min_player' || err.kind === 'schema_too_new') {
      return `CATALOG TOO NEW: ${err.message} (running v${VERSION}). Please update the player.`;
    }
    if (err.kind === 'http_404' || err.status === 404 || err.kind === 'race_not_found') {
      return `HTTP 404: ${err.message}`;
    }
    if (err.kind === 'network') {
      return `CATALOG/DATA HOST UNREACHABLE: ${err.message}`;
    }
    return `LOAD FAILED: ${err.message}`;
  }
  const s = String(err);
  // only a real HTTP status makes this a 404 card — a "404" inside a path, byte count or ETag
  // must not (the loader/pump/tile errors all carry the status as "HTTP <code>")
  if (/\bHTTP 404\b/.test(s)) return `HTTP 404: ${s}`;
  if (/Failed to fetch|NetworkError|network|unreachable/i.test(s)) return `DATA HOST UNREACHABLE: ${s}`;
  return `LOAD FAILED: ${s}`;
}
